// Shared chauffeur/tour split math — single source of truth used by the
// booking split section AND the 3rd-Party Owners balance panel so both
// always show the same numbers.
export const num = (n) => Number(n || 0);

// Driver's hourly rate for this booking type (from the Drivers tab).
export const driverRateFor = (booking, driver) =>
  booking?.booking_type === "tour" ? num(driver?.tour_rate) : num(driver?.rate);

// Hours for a booking: service_hours field first (auto-calc w/ override),
// then start→end datetimes, then the legacy `duration` field.
export const bookingHours = (b) => {
  const sh = num(b?.service_hours);
  if (sh > 0) return sh;
  const s = Date.parse(b?.start_datetime || "");
  const e = Date.parse(b?.end_datetime || "");
  if (!Number.isNaN(s) && !Number.isNaN(e) && e > s) return (e - s) / 3600000;
  const d = Number(b?.duration);
  return Number.isFinite(d) && d > 0 ? d : 0;
};

// What the driver earns on this booking: hours × hourly rate.
// Falls back to the flat rate when the booking has no hours info.
export const driverPayFor = (booking, driver) => {
  const rate = driverRateFor(booking, driver);
  if (!rate) return 0;
  const hrs = bookingHours(booking);
  return hrs > 0 ? Math.round(hrs * rate * 100) / 100 : rate;
};

// Driver cost with fallback to hours × the driver's rate when the booking has
// none. Once a split is confirmed we trust the stored value as-is (0 can mean
// "included in the split").
export const effectiveDriverCost = (b, driver) => {
  const dc = num(b.driver_cost);
  if (dc > 0 || b.split_confirmed) return dc;
  return driverPayFor(b, driver) || 0;
};

export function computeChauffeurSplit(b, v, driver) {
  const total = num(b.total) || num(b.amount);
  const deposit = num(b.depo_sent) || num(b.deposit_amount);
  const balance = Math.max(total - deposit, 0);
  const driverCost = effectiveDriverCost(b, driver);
  const gas = num(b.gas_cost);
  const additional = num(b.other_costs);
  const totalCosts = driverCost + gas + additional;
  const netProfit = total - totalCosts;

  const isThirdParty = v?.ownership === "third_party";
  const splitType = v?.owner_split_type || "percent";
  const splitPct = num(v?.owner_split_percent);
  const isBaseRate = isThirdParty && splitType === "base_rate";

  let ownerShare = 0;
  let companyShare = +netProfit.toFixed(2);
  if (isThirdParty) {
    if (isBaseRate) {
      // Owner takes their flat base and covers driver + gas out of it.
      ownerShare = num(b.owner_base_rate);
      companyShare = +(total - ownerShare).toFixed(2);
    } else {
      const base = splitType === "percent_total" ? total : netProfit;
      ownerShare = +((base * splitPct) / 100).toFixed(2);
      companyShare = +(netProfit - ownerShare).toFixed(2);
    }
  }

  const ownerDrove = !!b.owner_drove;
  const driverIsOwner = !!b.driver_is_owner;
  const ownerTotalTake = +(ownerShare + (ownerDrove && !isBaseRate ? driverCost : 0)).toFixed(2);

  const depositHeldBy = b.deposit_held_by || "company";
  const balanceHeldBy = b.balance_held_by || "company";
  const moneyCompany = (depositHeldBy === "company" ? deposit : 0) + (balanceHeldBy === "company" ? balance : 0);
  const moneyDriver = (depositHeldBy === "driver" ? deposit : 0) + (balanceHeldBy === "driver" ? balance : 0);

  // What the person on the road should end up holding: their fee + expenses
  // they fronted (+ the owner split when they ARE the owner).
  const driverEntitlement = driverIsOwner
    ? (isBaseRate ? ownerShare : +(driverCost + gas + additional + ownerShare).toFixed(2))
    : +(driverCost + gas + additional).toFixed(2);
  const driverNet = +(moneyDriver - driverEntitlement).toFixed(2); // > 0 → driver owes company

  return {
    total, deposit, balance, driverCost, gas, additional, totalCosts, netProfit,
    isThirdParty, isBaseRate, splitType, splitPct, ownerShare, companyShare,
    ownerDrove, driverIsOwner, ownerTotalTake, moneyCompany, moneyDriver,
    driverEntitlement, driverNet,
  };
}

// Per-trip owner ↔ company balance for the 3rd-Party Owners panel.
// Returns { weOwe, theyOwe } — mirrors exactly what the booking's split
// section banners say.
export function ownerTripBalance(b, v, driver) {
  if (b.settlement_override) {
    const amt = num(b.override_owner_amount);
    if (b.override_owner_direction === "company_owes_owner") return { weOwe: amt, theyOwe: 0, override: true };
    if (b.override_owner_direction === "owner_owes") return { weOwe: 0, theyOwe: amt, override: true };
    return { weOwe: 0, theyOwe: 0, override: true };
  }
  const s = computeChauffeurSplit(b, v, driver);
  const ownerSideDrove = s.driverIsOwner || s.ownerDrove;
  if (ownerSideDrove) {
    // Owner (or their driver) drove: entitled to their split + driver fee +
    // expenses fronted, minus whatever money they already hold.
    const entitlement = s.isBaseRate ? s.ownerShare : +(s.ownerShare + s.driverCost + s.gas + s.additional).toFixed(2);
    const net = +(entitlement - s.moneyDriver).toFixed(2);
    return net >= 0 ? { weOwe: net, theyOwe: 0 } : { weOwe: 0, theyOwe: -net };
  }
  // Our driver drove — the owner is simply owed their split. Money our own
  // driver holds is handled in the driver payout flow, not here.
  return { weOwe: s.ownerShare, theyOwe: 0 };
}
